<template>
    <section class="tk-insight-summary">
        <div class="tk-card-header">
            <div>
                <h2><q-icon name="auto_awesome" size="18px" /> Suggested next steps</h2>
                <p>From the latest reviewed engagement insight.</p>
            </div>
            <q-btn flat dense no-caps color="primary" label="View insights →" @click="$emit('open')" />
        </div>

        <q-banner v-if="error" rounded class="bg-red-1 text-negative">{{ error }}</q-banner>
        <div v-else-if="loading" class="tk-insight-summary__loading">
            <q-spinner color="primary" size="24px" />
        </div>
        <div
            v-else
            class="tk-insight-summary__content"
            role="button"
            tabindex="0"
            @click="$emit('open')"
            @keydown.enter.prevent="$emit('open')"
            @keydown.space.prevent="$emit('open')"
        >
            <template v-if="nextSteps">
                <p class="tk-insight-summary__narrative">{{ narrativeWithoutCitationMarkers(nextSteps.narrative) }}</p>
                <span v-if="overview.has_new_case_notes" class="tk-insight-summary__freshness">
                    <q-icon name="update" /> New case notes have been added since this insight was generated.
                </span>
            </template>
            <span v-else-if="overview.draft" class="tk-insight-summary__empty">
                You have a private insight draft ready to continue.
            </span>
            <span v-else class="tk-insight-summary__empty">
                No published insight yet. Open Insights to create one from eligible case notes.
            </span>
        </div>
    </section>
</template>

<script>
import api from "@/services/api/api";
import { insightSection, narrativeWithoutCitationMarkers } from "@/services/util/engagementInsights";

const emptyOverview = () => ({
    draft: null,
    latest: null,
    history: [],
    eligible_source_coverage: { count: 0, start_at: null, end_at: null },
    has_new_case_notes: false,
});

export default {
    name: "TkEngagementInsightSummary",
    emits: ["open"],
    props: {
        personId: {
            type: Number,
            required: true,
        },
        engagementId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            error: null,
            overview: emptyOverview(),
        };
    },
    computed: {
        nextSteps() {
            return this.overview.latest
                ? insightSection(this.overview.latest.content, "ai_suggested_next_steps")
                : null;
        },
    },
    async mounted() {
        await this.refresh();
    },
    methods: {
        narrativeWithoutCitationMarkers,
        errorMessage(error) {
            const statusText = error?.response?.statusText;
            return statusText && statusText !== "Internal Server Error"
                ? statusText
                : "Failed to load engagement insights.";
        },
        async refresh() {
            this.loading = true;
            this.error = null;
            try {
                const response = await api.tkPeople.getEngagementInsights(this.personId, this.engagementId);
                this.overview = response.data;
            } catch (error) {
                this.error = this.errorMessage(error);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.tk-insight-summary .tk-card-header {
    margin-bottom: 8px;
}

.tk-insight-summary .tk-card-header h2 {
    display: flex;
    align-items: center;
    gap: 6px;
}

.tk-insight-summary .tk-card-header p {
    margin: 4px 0 0;
    color: #748078;
    font-size: 0.8rem;
}

.tk-insight-summary__loading {
    display: flex;
    justify-content: center;
    padding: 18px;
}

.tk-insight-summary__content {
    display: block;
    width: 100%;
    padding: 14px;
    border: 1px solid #eadbbf;
    border-radius: 8px;
    background: #fbf5e9;
    cursor: pointer;
    text-align: left;
}

.tk-insight-summary__content:hover {
    border-color: #c7aa77;
}

.tk-insight-summary__content:focus-visible {
    outline: 2px solid #2f6f5e;
    outline-offset: 2px;
}

.tk-insight-summary__freshness {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 12px;
    color: #80602f;
    font-size: 0.78rem;
}

.tk-insight-summary__narrative {
    margin: 0;
    color: #35453e;
    font-size: 0.94rem;
    line-height: 1.65;
}

.tk-insight-summary__empty {
    color: #68756f;
    font-size: 0.88rem;
}
</style>
